import React from "react";
import type { DiffEntry, StepDiff } from "@veyso/core";
import { Eyebrow } from "../components/ui.js";
import { formatKey, formatValue } from "./format.js";

const Group: React.FC<{ title: string; entries: DiffEntry[]; tone: string }> = ({ title, entries, tone }) =>
  entries.length === 0 ? null : (
    <div className="flex flex-col gap-1.5">
      <Eyebrow>
        {title} · {entries.length}
      </Eyebrow>
      {entries.map((entry) => (
        <div key={`${title}-${entry.key}`} className="rounded-[6px] border border-[var(--edge)] p-2">
          <span className={`text-[11px] font-medium ${tone}`}>{entry.label}</span>
          {entry.before ? (
            <p className="mt-1 whitespace-pre-wrap break-words text-[12px] text-ink-400 line-through">{entry.before}</p>
          ) : null}
          {entry.after ? (
            <p className="mt-1 whitespace-pre-wrap break-words text-[12px] text-ink-100">{entry.after}</p>
          ) : null}
        </div>
      ))}
    </div>
  );

/**
 * How many timeline versions passed between two recorded runs.
 *
 * Saving from the editor writes a new timeline version through the API, which
 * records no step, so two consecutive `timeline-built` runs routinely read
 * version 3 then version 7. That is four ordinary saves, not four lost runs, and
 * without saying so the gap looks like a hole in the history.
 */
const versionGap = (diff: StepDiff): number => {
  if (diff.kind !== "timeline") return 0;
  const { versionBefore, versionAfter } = diff.stats;
  if (typeof versionBefore !== "number" || typeof versionAfter !== "number") return 0;
  return Math.max(0, versionAfter - versionBefore - 1);
};

/** One component for every artifact type: the differs already normalised them. */
export const StepDiffView: React.FC<{ diff: StepDiff }> = ({ diff }) => {
  const unchanged = diff.added.length + diff.removed.length + diff.changed.length === 0;
  const skippedVersions = versionGap(diff);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {Object.entries(diff.stats).map(([key, value]) => (
          <span key={key} className="text-[11px] text-ink-400">
            {formatKey(key)} <span className="tnum text-ink-200">{formatValue(key, value)}</span>
          </span>
        ))}
      </div>

      {skippedVersions > 0 ? (
        <p className="text-[11px] leading-relaxed text-ink-400">
          {skippedVersions} timeline {skippedVersions === 1 ? "version" : "versions"} in between came from the editor.
          Editing saves through the API rather than the pipeline, so those versions have no run of their own here.
        </p>
      ) : null}

      {unchanged ? (
        <p className="text-[12px] text-ink-400">Nothing changed between these two runs.</p>
      ) : (
        <>
          <Group title="Changed" entries={diff.changed} tone="text-scope-300" />
          <Group title="Added" entries={diff.added} tone="text-signal-ok" />
          <Group title="Removed" entries={diff.removed} tone="text-signal-bad" />
        </>
      )}
    </div>
  );
};
